<template>
  <div class="grid grid-cols-1 [grid-template-areas:'reviews-header''image''reviews'] gap-y-4 lg:gap-y-0 lg:[grid-template-areas:'reviews-header_image''reviews_image'] lg:grid-cols-2 lg:gap-x-16 lg:grid-rows-[auto_1fr]">
    <div class="[grid-area:reviews-header] pt-4">
      <THeading
        v-if="title"
        tag="h2"
        :level="3"
        class="mb-2"
      >
        {{ title }}
      </THeading>

      <p
        v-if="description"
        class="text-gray-500"
      >
        {{ description }}
      </p>
    </div>

    <div class="[grid-area:image]">
      <div class="relative aspect-4/3 lg:aspect-auto lg:h-full lg:min-h-100">
        <AtomTransition appear>
          <img
            :key="reviewIndex"
            :src="items[reviewIndex]?.img"
            :alt="items[reviewIndex]?.alt"
            loading="lazy"
            class="absolute inset-0 w-full h-full object-cover rounded-lg"
          >
        </AtomTransition>
      </div>
    </div>

    <div class="[grid-area:reviews] content-center lg:py-4">
      <div
        ref="reviewsContainerRef"
        class="flex gap-2 overflow-x-auto snap-x snap-mandatory scrollbar-hidden items-center"
      >
        <div
          v-for="(testimonial, contentIndex) in items"
          :key="contentIndex"
          ref="reviewsContentRefs"
          class="w-full h-full shrink-0 snap-center snap-always"
        >
          <TCard>
            <q class="block text-gray-500 text-center mb-4">
              {{ testimonial.content }}
            </q>

            <div class="flex items-center justify-center gap-4">
              <TIcon
                v-if="showCanadianFlag"
                :icon="['fab', 'canadian-maple-leaf']"
                size="2xl"
                class="text-canada-red"
              />

              <div>
                <span class="block font-medium">
                  {{ testimonial.name }} - {{ testimonial.location }}
                </span>
                <span class="text-gray-500 text-sm">
                  {{ getSinceLabel(testimonial.date, testimonial.userType) }}
                </span>
              </div>
            </div>
          </TCard>
        </div>
      </div>

      <div
        v-if="items.length > 1"
        class="mt-4 flex items-center justify-between gap-4"
      >
        <div class="flex items-center gap-4">
          <button
            v-for="(hostReview, index) in items"
            :key="hostReview.img"
            type="button"
            :class="[
              'size-2.5 rounded-full cursor-pointer',
              {
                'bg-primary-950': reviewIndex === index,
                'bg-gray-200': reviewIndex !== index,
              },
            ]"
            @click="goToReview(index)"
          >
            <span class="sr-only">{{ index }}</span>
          </button>
        </div>

        <div class="flex items-center gap-4">
          <TButton
            variant="secondary"
            thin
            @click="goPreviousReview"
          >
            <TIcon :icon="['fas', 'chevron-left']" />
          </TButton>

          <TButton
            variant="secondary"
            thin
            @click="goNextReview"
          >
            <TIcon :icon="['fas', 'chevron-right']" />
          </TButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TestimonialItem } from './types'

defineProps<{
  title: string
  description: string
  items: TestimonialItem[]
  showCanadianFlag?: boolean
}>()

const { t } = useI18n()

const reviewsContainerRef = useTemplateRef('reviewsContainerRef')
const reviewsContentRefs = useTemplateRef('reviewsContentRefs')
const $reviewsContentRefs = computed(() => reviewsContentRefs.value ?? [])

const {
  currentIndex: reviewIndex,
  next: goNextReview,
  previous: goPreviousReview,
  scrollToIndex: goToReview,
} = useSwipeContent(
  reviewsContainerRef,
  $reviewsContentRefs,
)

function getSinceLabel(date: TestimonialItem['date'], userType?: TestimonialItem['userType']) {
  if (!userType) {
    return t('joinedIn', { date })
  }

  if (userType === 'host') {
    return t('hostingSince', { date })
  }

  return t('guestSince', { date })
}
</script>

<i18n lang="json">
  {
    "en": {
      "hostingSince": "Hosting since {date}",
      "guestSince": "Guest since {date}",
      "joinedIn": "Joined in {date}"
    },
    "fr": {
      "hostingSince": "Hôte depuis {date}",
      "guestSince": "Voyageur depuis {date}",
      "joinedIn": "Membre depuis {date}"
    }
  }
</i18n>
